export const M6_UNIT_5 = {
  id: "m6-u5-should-advice",
  title: "Unit 5: Giving Advice with SHOULD",
  topic: "Learn to give and ask for advice naturally! SHOULD = the best thing to do. SHOULDN'T = what to avoid. Essential for helping friends and sounding caring in English.",
  iconType: "lightbulb",
  grammarImage: "/grammar/grammar-m6-u5.jpg",
  grammarText: "Use SHOULD to give advice — what is the best thing to do. Use SHOULDN'T for what to avoid. SHOULD + base verb. SHOULD never changes form with the subject.",

  sets: [

    // ===================================================================
    // SET 1: WHAT IS SHOULD? (A2)
    // ===================================================================
    {
      id: "m6-u5-s1",
      title: "Set 1: What is SHOULD?",
      context: `*Toronto — Week 17, Monday morning*

Sofía has a headache. She didn't sleep well and she has class in one hour.
Her roommate looks at her. "You should take some medicine," she says. "And you should drink some water."
Sofía nods. "I know. I should go to bed earlier."
"Yes," her roommate says. "You should!"

*Toronto — Semana 17, lunes por la mañana*

Sofía tiene dolor de cabeza. No durmió bien y tiene clase en una hora.
Su compañera de cuarto la mira. "Deberías tomar medicina," dice. "Y deberías beber agua."
Sofía asiente. "Lo sé. Debería acostarme más temprano."
"Sí," dice su compañera. "¡Deberías!"`,

      questions: [
        {
          type: "mc",
          q: "Your friend is sick. What is the best advice?",
          options: ["You should see a doctor.", "You might see a doctor.", "You will see a doctor."],
          a: "You should see a doctor.",
          tip: "SHOULD = giving advice. It means this is the best thing to do.",
          successTip: "SHOULD = the advice modal 💡. 'You should see a doctor' = deberías ver a un doctor. SHOULD is softer than MUST (obligation) — it's a recommendation, not a rule. Natives use SHOULD constantly for friendly, caring advice. It's one of the most useful words in everyday English!"
        },
        {
          type: "order",
          q: "Traduce: Deberías estudiar esta noche.",
          words: ["You", "should", "study", "tonight."],
          a: "You should study tonight.",
          extra: ["must", "might", "to"],
          tip: "SHOULD + base verb. No 'to', no '-ing'.",
          successTip: "'You should study tonight' = deberías estudiar esta noche 📚. Structure: subject + SHOULD + base verb + rest. Very clean and simple. SHOULD gives advice for the present or near future. 'Tonight' is the time context, but the structure stays the same!"
        },
        {
          type: "match",
          q: "Match the problems to the correct SHOULD advice:",
          pairs: [
            { left: "I'm tired", right: "You should rest" },
            { left: "I'm hungry", right: "You should eat" },
            { left: "I'm cold", right: "You should wear a jacket" }
          ],
          a: "completed",
          successTip: "Problem → SHOULD + solution = the basic advice pattern! 🎯 This is the heart of Unit 5. Someone tells you a problem → you give advice with SHOULD. 'I'm tired' → 'You should rest.' Simple, natural, caring. You'll use this pattern every day in English conversation!"
        },
        {
          type: "input",
          q: "It's raining outside. You ___ take an umbrella.",
          a: "should",
          alts: ["Should"],
          tip: "Rain = practical advice. Use SHOULD for the best thing to do.",
          successTip: "'You should take an umbrella' = deberías llevar un paraguas ☂️. SHOULD for practical, common-sense advice. The situation is clear — it's raining — so the advice is obvious. SHOULD is perfect for this kind of logical, caring recommendation. Very natural!"
        },
        {
          type: "mc",
          q: "Which sentence gives ADVICE (not obligation)?",
          options: ["You should try this.", "You must try this.", "You will try this."],
          a: "You should try this.",
          tip: "SHOULD = soft advice (suggestion). MUST = strong obligation (required).",
          successTip: "SHOULD vs MUST = advice vs obligation ⚠️. 'You should try this' = it's a good idea (my recommendation). 'You must try this' = it's required/necessary. SHOULD is friendly and caring. MUST can sound demanding or like a rule. For friendly advice between friends, SHOULD is almost always the right choice!"
        },
        {
          type: "order",
          q: "Traduce: Deberías beber agua.",
          words: ["You", "should", "drink", "water."],
          a: "You should drink water.",
          extra: ["must", "might", "to"],
          tip: "Simple health advice. SHOULD + base verb.",
          successTip: "'You should drink water' = deberías beber agua 💧. Short, simple, perfect advice. SHOULD + short base verb + simple noun = one of the most common advice structures in English. Health, food, sleep, exercise — all use this exact pattern. Clean and direct!"
        },
        {
          type: "mc",
          q: "SHOULD + ___ form of the verb:",
          options: ["base verb", "verb + -ing", "to + verb"],
          a: "base verb",
          tip: "SHOULD + BASE VERB. Like all modal verbs — no 'to', no '-ing'.",
          successTip: "SHOULD + base verb — always! ✅ 'You should go' ✓ / 'You should to go' ❌ / 'You should going' ❌. This rule applies to ALL modal verbs (can, will, might, should). They ALL take the base verb directly. No exceptions. Learn it once and it works for every modal!"
        },
        {
          type: "input",
          q: "You look stressed. You ___ take a break.",
          a: "should",
          alts: ["Should"],
          tip: "Stressed = not OK. Taking a break is clearly the best thing = SHOULD.",
          successTip: "'You should take a break' = deberías tomarte un descanso 😌. 'Take a break' = tomarse un descanso. Very common advice in work and study contexts. SHOULD for advice that is clearly the right thing to do in the situation. Caring and direct at the same time!"
        },
        {
          type: "mc",
          q: "SHOULD doesn't change with the subject. Which is correct?",
          options: [
            "He should / She should / They should (all correct)",
            "He shoulds / She shoulds",
            "He should / She shoulds"
          ],
          a: "He should / She should / They should (all correct)",
          tip: "SHOULD = same for ALL subjects. Modal verbs never change form.",
          successTip: "Modal verbs never add -s for third person 🔑. 'He should' / 'She should' / 'It should' — all the same. NEVER 'he shoulds' or 'she shoulds'. This rule is universal for ALL modals: can, will, might, should. No exceptions. No -s, no -ing, no changes — ever!"
        },
        {
          type: "order",
          q: "Traduce: Deberías practicar inglés todos los días.",
          words: ["You", "should", "practice", "English", "every", "day."],
          a: "You should practice English every day.",
          extra: ["must", "might", "to"],
          tip: "General good habit advice = SHOULD.",
          successTip: "'Practice every day' = the classic language learning advice 📚. 'You should practice English every day' = deberías practicar inglés todos los días. SHOULD for habits and general recommendations works perfectly with time expressions like 'every day', 'always', 'regularly'. Very natural!"
        }
      ]
    },

    // ===================================================================
    // SET 2: SHOULDN'T — WHAT TO AVOID (A2)
    // ===================================================================
    {
      id: "m6-u5-s2",
      title: "Set 2: SHOULDN'T",
      context: `*Toronto — Week 17, Tuesday*

Dan is making some bad decisions. He wants to drive after two beers. He wants to eat fast food for every meal. He wants to stay up until 3am before an exam.
His friends are worried. "Dan, you shouldn't do that," Sofía says.
"Why not?" he laughs.
"Because," Priya says, "we like you and we don't want you to fail — or worse!"

*Toronto — Semana 17, martes*

Dan está tomando malas decisiones. Quiere manejar después de dos cervezas. Quiere comer comida rápida en cada comida. Quiere quedarse despierto hasta las 3am antes de un examen.
Sus amigos están preocupados. "Dan, no deberías hacer eso," dice Sofía.
"¿Por qué no?" se ríe.
"Porque," dice Priya, "nos caes bien y no queremos que reprobes — ¡ni algo peor!"`,

      questions: [
        {
          type: "mc",
          q: "Your friend wants to drive after drinking. What do you say?",
          options: ["You shouldn't drive.", "You should drive.", "You might drive."],
          a: "You shouldn't drive.",
          tip: "SHOULDN'T = strong advice NOT to do something. For safety and health warnings.",
          successTip: "SHOULDN'T = no deberías ⚠️. Use it when something is clearly a bad idea — especially for safety, health, and relationships. 'You shouldn't drive after drinking' = strong, caring advice. SHOULDN'T is more urgent and serious than MIGHT NOT. It means: this is genuinely not a good idea!"
        },
        {
          type: "order",
          q: "Traduce: No deberías comer tanta comida rápida.",
          words: ["You", "shouldn't", "eat", "so", "much", "junk", "food."],
          a: "You shouldn't eat so much junk food.",
          extra: ["mustn't", "won't", "not"],
          tip: "'Junk food' = comida chatarra. SHOULDN'T for food/health advice.",
          successTip: "'Junk food' = comida chatarra 🍔. SHOULDN'T for health warnings is very common in English. 'So much' = tanto. 'So much junk food' / 'so many chips' — use 'so much' with uncountable nouns. Health advice = SHOULDN'T + eat/drink/smoke/skip exercise. Very natural pattern!"
        },
        {
          type: "input",
          q: "It's very late. You ___ go to bed so late.",
          a: "shouldn't",
          alts: ["Shouldn't"],
          tip: "Late bedtime = bad habit. SHOULDN'T = advice to stop doing something.",
          successTip: "'You shouldn't go to bed so late' = no deberías acostarte tan tarde 🌙. SHOULDN'T for habits you want someone to change. Compare: 'You should go to bed earlier' (positive advice) vs 'You shouldn't go to bed so late' (negative advice). Same meaning — two different ways to say it!"
        },
        {
          type: "mc",
          q: "SHOULDN'T = SHOULD + ___",
          options: ["not", "to not", "no"],
          a: "not",
          tip: "SHOULDN'T = SHOULD + NOT (contracted). One word in casual speech.",
          successTip: "SHOULDN'T = should not (contracted) 🔗. In spoken English, 'shouldn't' is used 95% of the time — 'should not' sounds more formal or emphatic. 'You should not do that!' (very emphatic/serious). 'You shouldn't do that' (normal friendly advice). Both correct — 'shouldn't' is more natural day to day!"
        },
        {
          type: "order",
          q: "Traduce: No deberías conducir rápido. Las calles están mojadas.",
          words: ["You", "shouldn't", "drive", "fast.", "The", "streets", "are", "wet."],
          a: "You shouldn't drive fast. The streets are wet.",
          extra: ["mustn't", "won't", "not"],
          tip: "Safety advice: reason comes after. SHOULDN'T + reason = natural structure.",
          successTip: "SHOULDN'T + reason = very natural advice structure 🚗. 'You shouldn't drive fast. The streets are wet.' Two sentences: advice + reason. The reason explains WHY. This pattern sounds genuine and caring — not bossy. Always giving a reason makes your advice more convincing and natural!"
        },
        {
          type: "mc",
          q: "Your friend is sick. Which advice is WRONG?",
          options: ["You shouldn't rest.", "You shouldn't go to work.", "You shouldn't drink cold water."],
          a: "You shouldn't rest.",
          tip: "Sick people SHOULD rest! 'You shouldn't rest' is backwards advice.",
          successTip: "Logic check! 🤔 When sick: ✅ 'You should rest.' ❌ 'You shouldn't rest.' Resting is clearly the right thing to do — so SHOULD (not SHOULDN'T) is correct. Always check: is this action GOOD or BAD for the person? Good → SHOULD. Bad → SHOULDN'T. Simple logic test every time!"
        },
        {
          type: "input",
          q: "You have an exam tomorrow. You ___ stay up all night.",
          a: "shouldn't",
          alts: ["Shouldn't"],
          tip: "Staying up all night before an exam = bad idea = SHOULDN'T.",
          successTip: "'Stay up' = quedarse despierto hasta tarde 🌙. 'You shouldn't stay up all night' = classic student advice. 'Stay up' is a very common phrasal verb. 'Stay up late', 'stay up all night', 'stay up until 3am'. SHOULDN'T + stay up = natural combination for sleep/study advice!"
        },
        {
          type: "order",
          q: "Traduce: No deberías comprar tantas cosas. No tienes dinero.",
          words: ["You", "shouldn't", "buy", "so", "many", "things.", "You", "don't", "have", "money."],
          a: "You shouldn't buy so many things. You don't have money.",
          extra: ["mustn't", "won't", "not"],
          tip: "'So many things' = tantas cosas (countable). Reason after = natural.",
          successTip: "'So much' vs 'so many' 📊: 'so much' + uncountable (money, time, food). 'so many' + countable (things, people, books). 'You shouldn't buy so many things' = no deberías comprar tantas cosas. Adding the reason 'You don't have money' makes the advice feel natural and caring — not critical!"
        },
        {
          type: "match",
          q: "Match the situations to the correct SHOULDN'T advice:",
          pairs: [
            { left: "It's raining", right: "You shouldn't forget your umbrella" },
            { left: "You're tired", right: "You shouldn't skip sleep" },
            { left: "You have an exam", right: "You shouldn't watch TV all night" }
          ],
          a: "completed",
          successTip: "SHOULDN'T for practical daily warnings 🎯. These three situations are very common in real life — and so is the advice. 'Shouldn't forget' / 'shouldn't skip' / 'shouldn't watch TV all night' = the advice triangle for any busy student. Natural, caring, and direct!"
        },
        {
          type: "mc",
          q: "'I ate too much.' Most natural response from a friend:",
          options: ["You shouldn't eat so much next time.", "You shouldn't have eaten.", "You shouldn't eating."],
          a: "You shouldn't eat so much next time.",
          tip: "Only use grammar you know! SHOULDN'T + base verb + time expression = correct.",
          successTip: "'You shouldn't eat so much next time' = no deberías comer tanto la próxima vez 🍽️. 'Next time' turns the advice forward — towards the future. Very caring and practical. We stay with SHOULDN'T + base verb, which is the grammar you know. Simple, correct, and completely natural!"
        }
      ]
    },

    // ===================================================================
    // SET 3: SHOULD IN QUESTIONS — ASKING FOR ADVICE (A2-B1)
    // ===================================================================
    {
      id: "m6-u5-s3",
      title: "Set 3: Asking for Advice",
      context: `*Toronto — Week 18, Wednesday*

Leo has a problem: he got a job offer, but he also got accepted to a Master's program. Both start in September.
He doesn't know what to do.
"Should I take the job?" he asks the group.
"What should I do?" he asks Professor Chen.
"I don't know," he texts his mum. "Should I call you?"
Everyone has advice. But first, Leo has to ask!

*Toronto — Semana 18, miércoles*

Leo tiene un problema: recibió una oferta de trabajo, pero también fue aceptado en un máster. Ambos empiezan en septiembre.
No sabe qué hacer.
"¿Debería aceptar el trabajo?" le pregunta al grupo.
"¿Qué debería hacer?" le pregunta a la profesora Chen.
"No sé," le escribe a su mamá. "¿Debería llamarte?"
Todos tienen consejos. ¡Pero primero Leo tiene que preguntar!"`,

      questions: [
        {
          type: "mc",
          q: "You want advice about a job offer. How do you ask?",
          options: ["Should I take the job?", "I should take the job?", "Do I should take the job?"],
          a: "Should I take the job?",
          tip: "SHOULD questions = SHOULD + subject + verb. NEVER 'do I should'.",
          successTip: "SHOULD questions = inversion! 🔄 'Should I...?' = ¿Debería yo...? The structure is: SHOULD + subject + base verb. No 'do' — modal verbs form their own questions by moving to the front. 'Do I should?' ❌ is the most common error. 'Should I?' ✓ is clean and correct. Very common for asking advice!"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué debería hacer?",
          words: ["What", "should", "I", "do?"],
          a: "What should I do?",
          extra: ["must", "might", "can"],
          tip: "WH- word + SHOULD + subject + base verb = advice question.",
          successTip: "'What should I do?' = ¿Qué debería hacer? 🤔. One of the MOST useful phrases in English — you'll say this constantly. Structure: WH-word + SHOULD + I + base verb. All WH- words work: What / Where / When / Why / Which / How. Learn this pattern once and ask for advice about anything!"
        },
        {
          type: "input",
          q: "___ I bring anything to the party?",
          a: "Should",
          alts: ["should"],
          tip: "Asking for advice about the party: SHOULD at the start of the question.",
          successTip: "'Should I bring anything to the party?' = ¿Debería llevar algo a la fiesta? 🎉. Very common and polite social question. In North American culture, asking 'Should I bring something?' when invited to someone's home is considered thoughtful and considerate. Use it often!"
        },
        {
          type: "mc",
          q: "Which question is WRONG?",
          options: ["Do I should go?", "Should I go?", "Where should I go?"],
          a: "Do I should go?",
          tip: "NEVER 'do I should'. Modal verbs never use DO for questions.",
          successTip: "'Do I should...?' ❌ = the #1 SHOULD question error for Spanish speakers 🇲🇽. This comes from translating '¿Debo...?' directly. In English, modal verbs NEVER use DO to form questions. SHOULD moves to the front: 'Should I go?' ✓. 'Do I should go?' ❌. Simple rule — modals make their own questions!"
        },
        {
          type: "order",
          q: "Traduce: ¿Cuál debería aceptar?",
          words: ["Which", "one", "should", "I", "accept?"],
          a: "Which one should I accept?",
          extra: ["must", "might", "do"],
          tip: "WHICH + SHOULD = choosing between options. Very common for decisions.",
          successTip: "'Which one should I accept?' = ¿Cuál debería aceptar? 🤔. 'Which one' is perfect when you have two or more specific options. 'Which one should I choose / take / buy?' — all natural. Compare with 'What should I do?' (open question). 'Which one' = specific choice. Very useful!"
        },
        {
          type: "mc",
          q: "'I don't know what to wear.' A natural question back:",
          options: ["What should you wear?", "What do you should wear?", "What you should wear?"],
          a: "What should you wear?",
          tip: "WH- + SHOULD + subject + verb. No DO, no subject before SHOULD.",
          successTip: "'What should you wear?' = ¿Qué deberías ponerte? 👗. Notice: the question word order is the same whether asking about yourself OR others. 'What should I wear?' / 'What should she wear?' / 'What should they wear?' — structure never changes. WH- + SHOULD + subject + verb. Always!"
        },
        {
          type: "input",
          q: "Where ___ we go for dinner?",
          a: "should",
          alts: ["Should"],
          tip: "Group decision question. SHOULD for asking what's best.",
          successTip: "'Where should we go for dinner?' = ¿Adónde deberíamos ir a cenar? 🍽️. One of the most useful questions in daily life! 'Where should we...?' is great for group decisions: restaurants, movies, activities. 'We' = the group asking together. Very common and very natural in everyday conversation!"
        },
        {
          type: "order",
          q: "Traduce: ¿Cuánto dinero debería llevar?",
          words: ["How", "much", "money", "should", "I", "take?"],
          a: "How much money should I take?",
          extra: ["must", "might", "do"],
          tip: "HOW MUCH + SHOULD = asking about quantities. Common for travel/shopping.",
          successTip: "'How much money should I take?' = ¿Cuánto dinero debería llevar? 💰. 'How much' + SHOULD = asking for a quantity recommendation. Also: 'How much should I eat?' / 'How much time should I spend?' / 'How many should I buy?' All natural advice questions. Very practical!"
        },
        {
          type: "match",
          q: "Match the question words with SHOULD to complete the advice question:",
          pairs: [
            { left: "¿Qué debería hacer?", right: "What should I do?" },
            { left: "¿Adónde deberíamos ir?", right: "Where should we go?" },
            { left: "¿Cuándo debería llegar?", right: "When should I arrive?" }
          ],
          a: "completed",
          successTip: "WH- words + SHOULD = unlimited advice questions 🎯. What / Where / When / Which / How + SHOULD + subject + base verb. Once you know this pattern, you can ask for advice about absolutely anything. Master this and you can navigate any uncertain situation in English — restaurants, travel, work, life!"
        },
        {
          type: "mc",
          q: "Short answer to 'Should I call him?' — if YES:",
          options: ["Yes, you should.", "Yes, you should call.", "Yes, should."],
          a: "Yes, you should.",
          tip: "SHORT ANSWER: Yes/No + subject + SHOULD. No need to repeat the verb.",
          successTip: "SHOULD short answers 💬: 'Should I go?' → 'Yes, you should.' / 'No, you shouldn't.' Clean, natural, complete. You don't need to repeat the verb in short answers — 'Yes, you should call' sounds slightly repetitive. 'Yes, you should.' (full stop) is perfect. Very common in casual conversation!"
        }
      ]
    },

    // ===================================================================
    // SET 4: SHOULD vs MUST vs MIGHT (B1)
    // ===================================================================
    {
      id: "m6-u5-s4",
      title: "Set 4: SHOULD vs MUST vs MIGHT",
      context: `*Toronto — Week 18, Thursday class*

Professor Chen writes three sentences on the board:
"You must wear a seatbelt." — "You should wear sunscreen." — "You might want a jacket."
"All three are advice," she says. "But they have very different levels of strength."
Sofía thinks: must = rule. Should = recommendation. Might = gentle suggestion.
"Exactly," Professor Chen smiles. "Now you can give advice at three different levels!"

*Toronto — Semana 18, jueves en clase*

La profesora Chen escribe tres oraciones en la pizarra:
"You must wear a seatbelt." — "You should wear sunscreen." — "You might want a jacket."
"Las tres son consejos," dice. "Pero tienen niveles de fuerza muy diferentes."
Sofía piensa: must = regla. Should = recomendación. Might = sugerencia suave.
"Exactamente," sonríe la profesora Chen. "¡Ahora puedes dar consejos en tres niveles diferentes!"`,

      questions: [
        {
          type: "mc",
          q: "What is the difference between SHOULD and MUST?",
          options: [
            "SHOULD = recommendation (a good idea). MUST = obligation (required).",
            "SHOULD = obligation. MUST = recommendation.",
            "They mean exactly the same thing."
          ],
          a: "SHOULD = recommendation (a good idea). MUST = obligation (required).",
          tip: "SHOULD = soft advice. MUST = strong rule or obligation. Big difference!",
          successTip: "The advice strength scale 💪: MIGHT (gentle suggestion) → SHOULD (recommendation) → MUST (obligation/rule). 'You should exercise' = it's a good idea. 'You must wear a seatbelt' = it's the law. Choosing the right level shows you understand the situation. SHOULD is the most common for friendly advice!"
        },
        {
          type: "match",
          q: "Match the modal to the correct strength level:",
          pairs: [
            { left: "MUST", right: "obligation — it's required" },
            { left: "SHOULD", right: "recommendation — it's a good idea" },
            { left: "MIGHT", right: "gentle suggestion — maybe consider it" }
          ],
          a: "completed",
          successTip: "Three modals, three levels of advice 🎯. MIGHT = I'm just suggesting (softest). SHOULD = I think this is the best thing to do (medium). MUST = you have to / it's a rule (strongest). In daily conversation, SHOULD is by far the most common. Save MUST for rules and urgent situations!"
        },
        {
          type: "mc",
          q: "Your friend wants to improve her English. Which is softest?",
          options: [
            "You might want to practice more.",
            "You should practice more.",
            "You must practice more."
          ],
          a: "You might want to practice more.",
          tip: "'You might want to...' is softer than SHOULD — a gentle, polite suggestion.",
          successTip: "'You might want to...' = a very polite, soft way to give advice 🤗. It's less direct than SHOULD. 'You might want to practice more' = tal vez quieras practicar más. Use this when you don't want to sound bossy. Natives use it constantly to soften advice — especially with people they don't know well!"
        },
        {
          type: "order",
          q: "Traduce: Debes usar cinturón de seguridad. Es la ley.",
          words: ["You", "must", "wear", "a", "seatbelt.", "It's", "the", "law."],
          a: "You must wear a seatbelt. It's the law.",
          extra: ["should", "might", "have"],
          tip: "LAW = obligation = MUST (not SHOULD). 'It's the law' confirms this.",
          successTip: "Laws and rules → MUST ⚖️. 'You must wear a seatbelt. It's the law.' The second sentence confirms why MUST is correct — it's not just a recommendation, it's legally required. 'You should wear a seatbelt' would suggest it's a good idea but optional — which is wrong for a legal requirement!"
        },
        {
          type: "input",
          q: "General advice for everyone: People ___ exercise regularly.",
          a: "should",
          alts: ["Should"],
          tip: "General health recommendation for all people = SHOULD. Not MUST (not a law).",
          successTip: "'People should exercise regularly' = las personas deberían ejercitarse regularmente 🏃. SHOULD for general advice that is a good idea for everyone. Not MUST (exercising isn't a law). Not MIGHT (it's more than a gentle suggestion). SHOULD = the balanced, sensible recommendation. Perfect for health, habits, and life tips!"
        },
        {
          type: "mc",
          q: "Which sentence is the most URGENT?",
          options: [
            "You must call an ambulance now!",
            "You should call a doctor.",
            "You might want to see someone."
          ],
          a: "You must call an ambulance now!",
          tip: "Emergency = MUST. Recommendation = SHOULD. Gentle suggestion = MIGHT.",
          successTip: "Emergency → MUST ⚠️. 'You must call an ambulance now!' — urgency, immediate action required. SHOULD would be too soft for a real emergency. 'You should call a doctor' = serious but not urgent. 'You might want to see someone' = very gentle, minor concern. The context tells you which modal to use!"
        },
        {
          type: "order",
          q: "Traduce: Quizás quieras llevar un paraguas. Está un poco nublado.",
          words: ["You", "might", "want", "to", "take", "an", "umbrella.", "It's", "a", "bit", "cloudy."],
          a: "You might want to take an umbrella. It's a bit cloudy.",
          extra: ["should", "must", "have"],
          tip: "'A bit cloudy' = not certain rain = gentle suggestion = MIGHT WANT TO.",
          successTip: "'You might want to take an umbrella' = softest advice level 💭. Compare the three levels for umbrella advice: 'You must take an umbrella' (mandatory — hurricane!). 'You should take an umbrella' (good idea — rainy forecast). 'You might want to take an umbrella' (gentle suggestion — bit cloudy). Perfect trio!"
        },
        {
          type: "mc",
          q: "'I'm thinking about trying sushi for the first time.' Best gentle advice:",
          options: [
            "You might want to try a small amount first.",
            "You must try sushi.",
            "You should definitely eat sushi."
          ],
          a: "You might want to try a small amount first.",
          tip: "First time trying something new = gentle suggestion = MIGHT WANT TO.",
          successTip: "'You might want to try a small amount first' = very considerate advice 🍣. When someone is nervous or trying something new, soft advice is kinder. MUST would be pushy. 'You should definitely' sounds bossy. 'You might want to' = I'm just suggesting, no pressure. Perfect for gentle, considerate advice!"
        },
        {
          type: "input",
          q: "At work, it's company policy: You ___ wear your ID badge.",
          a: "must",
          alts: ["Must"],
          tip: "Company policy / rule = MUST (not SHOULD — this is required, not optional).",
          successTip: "'You must wear your ID badge' = rules and policies → MUST 🏢. Company policies, school rules, legal requirements, safety regulations = MUST. If it's optional but a good idea = SHOULD. If it's just a gentle suggestion = MIGHT WANT TO. Always ask yourself: is this a rule or a recommendation?"
        },
        {
          type: "match",
          q: "Match the situation to the best modal:",
          pairs: [
            { left: "Legal requirement", right: "must" },
            { left: "Good daily habit", right: "should" },
            { left: "Polite soft suggestion", right: "might want to" }
          ],
          a: "completed",
          successTip: "The advice modal triangle mastered! 🔺 MUST = laws, rules, obligations. SHOULD = recommendations, good ideas, friendly advice. MIGHT WANT TO = soft, polite suggestions. Use these three together and you can give advice at exactly the right level for every situation. Very sophisticated English!"
        }
      ]
    },

    // ===================================================================
    // SET 5: HEALTH & WELLNESS ADVICE (B1)
    // ===================================================================
    {
      id: "m6-u5-s5",
      title: "Set 5: Health Advice",
      context: `*Toronto — Week 19, health class*

Priya is studying nutrition. She loves giving health advice.
"You should eat more vegetables," she tells Dan.
"You shouldn't skip breakfast," she tells Leo.
"You should drink eight glasses of water a day," she tells everyone.
"Priya," Dan says, "are you a doctor or a student?"
"Right now," she laughs, "I'm both!"

*Toronto — Semana 19, clase de salud*

Priya estudia nutrición. Le encanta dar consejos de salud.
"Deberías comer más verduras," le dice a Dan.
"No deberías saltarte el desayuno," le dice a Leo.
"Deberías beber ocho vasos de agua al día," le dice a todos.
"Priya," dice Dan, "¿eres doctora o estudiante?"
"Ahora mismo," se ríe, "¡las dos cosas!"`,

      questions: [
        {
          type: "mc",
          q: "'I have a terrible headache.' What is the best advice?",
          options: [
            "You should take some medicine and rest.",
            "You should took medicine.",
            "You might take medicine."
          ],
          a: "You should take some medicine and rest.",
          tip: "Clear health problem → clear advice = SHOULD. MIGHT would be too uncertain here.",
          successTip: "'You should take some medicine and rest' = deberías tomar medicina y descansar 💊. SHOULD for clear, practical health advice. Notice 'some medicine' — 'some' is used with uncountable nouns for things you can't count individually. 'Take some medicine' / 'drink some water' / 'eat some fruit' — very natural!"
        },
        {
          type: "order",
          q: "Traduce: Deberías quedarte en casa y descansar.",
          words: ["You", "should", "stay", "home", "and", "rest."],
          a: "You should stay home and rest.",
          extra: ["must", "might", "to"],
          tip: "Two pieces of advice connected with 'and'. SHOULD + base verb + and + base verb.",
          successTip: "Double advice with 'and' 🏠😴. 'You should stay home AND rest' = two actions in one sentence. Structure: SHOULD + verb + and + verb. Very efficient! 'Stay home and rest' / 'drink water and sleep' / 'eat well and exercise' — giving two related pieces of advice together is natural and common."
        },
        {
          type: "input",
          q: "'I can't sleep well.' You ___ cut down on coffee.",
          a: "should",
          alts: ["Should"],
          tip: "Sleep problem → reduce caffeine = clear health advice = SHOULD.",
          successTip: "'Cut down on' = reducir el consumo de algo 😴☕. 'Cut down on coffee / sugar / screen time.' 'Should + cut down on' = classic health advice. 'Cut down on' is a phrasal verb that means gradually reduce — not stop completely (that would be 'cut out'). Very common in health and nutrition advice!"
        },
        {
          type: "mc",
          q: "'I'm stressed all the time.' What is natural advice?",
          options: [
            "You should take up a hobby.",
            "You should stressed.",
            "You might stress."
          ],
          a: "You should take up a hobby.",
          tip: "'Take up' = start a new hobby or activity. SHOULD for clear stress advice.",
          successTip: "'Take up a hobby' = empezar un hobby 🎨. 'Take up' is a phrasal verb meaning to start doing a new activity. 'Take up yoga / painting / running / cooking.' Very common! 'You should take up a hobby' = great stress-relief advice. 'Take up' always refers to a NEW activity you start doing regularly."
        },
        {
          type: "order",
          q: "Traduce: Deberías ver a un médico. Llevas dos semanas con ese dolor.",
          words: ["You", "should", "see", "a", "doctor.", "You", "have", "that", "pain", "for", "two", "weeks."],
          a: "You should see a doctor. You have that pain for two weeks.",
          extra: ["must", "might", "to"],
          tip: "'See a doctor' (not 'go to the doctor'). Reason follows to explain the advice.",
          successTip: "'See a doctor' = ver a un doctor 👨‍⚕️. Use 'see a doctor' NOT 'go to the doctor' in most advice contexts — it's more natural. 'See' in this context means 'have an appointment with'. 'See a doctor / dentist / specialist.' Two weeks of pain = clear reason for the advice. SHOULD for serious but non-emergency advice!"
        },
        {
          type: "mc",
          q: "'I want to get healthier.' What is the best advice?",
          options: [
            "You should work out regularly.",
            "You should worked out.",
            "You should working out."
          ],
          a: "You should work out regularly.",
          tip: "'Work out' = exercise. SHOULD + base verb. Not '-ed' or '-ing'.",
          successTip: "'Work out' = hacer ejercicio 💪. 'Work out' is the most common informal phrase for exercising. 'I work out three times a week.' 'You should work out regularly.' 'Regularly' = con regularidad — better than 'every day' (too much pressure). 'Should + work out + regularly' = perfectly natural fitness advice!"
        },
        {
          type: "input",
          q: "'I'm always tired.' You ___ get more sleep.",
          a: "should",
          alts: ["Should"],
          tip: "Always tired = sleep deprivation. Getting more sleep = the obvious advice = SHOULD.",
          successTip: "'You should get more sleep' = deberías dormir más 😴. 'Get more sleep' vs 'sleep more' — both natural! 'Get more sleep' (recommended — treat sleep as something you obtain). 'Sleep more hours' also works. SHOULD for consistent patterns — 'always tired' = a regular problem that needs regular advice!"
        },
        {
          type: "order",
          q: "Traduce: Deberías comer más frutas y verduras.",
          words: ["You", "should", "eat", "more", "fruits", "and", "vegetables."],
          a: "You should eat more fruits and vegetables.",
          extra: ["must", "might", "to"],
          tip: "Classic nutrition advice. SHOULD for dietary recommendations.",
          successTip: "'You should eat more fruits and vegetables' = deberías comer más frutas y verduras 🥗. Classic nutrition advice — you'll hear this from doctors, nutritionists, and friends everywhere! 'Eat more + healthy food' / 'Eat less + unhealthy food' = two essential advice patterns. 'More' and 'less' are your best friends here!"
        },
        {
          type: "mc",
          q: "'My eyes hurt from the computer screen.' What do you advise?",
          options: [
            "You should take breaks from the screen.",
            "You should took breaks.",
            "You might take breaks."
          ],
          a: "You should take breaks from the screen.",
          tip: "Clear eye strain problem → clear advice = SHOULD. 'Take breaks from' = stop temporarily.",
          successTip: "'Take breaks from the screen' = tomar descansos de la pantalla 💻. Very modern, very relevant health advice! 'Take a break from' = alejarse temporalmente de algo. 'Take breaks from the screen / work / social media.' SHOULD for this kind of regular habit advice is perfect — it's not an emergency, but it's clearly the right thing to do!"
        },
        {
          type: "match",
          q: "Match the health problems to the correct advice:",
          pairs: [
            { left: "Overweight", right: "should work out more" },
            { left: "Can't sleep", right: "should cut down on coffee" },
            { left: "Always getting sick", right: "should see a doctor" }
          ],
          a: "completed",
          successTip: "Health advice patterns mastered! 💊 Work out more / cut down on / see a doctor — three essential health phrases. Notice all three use SHOULD (recommendations, not laws). Health advice is one of the most common uses of SHOULD in everyday English. Now you can give caring, natural health advice in any situation!"
        }
      ]
    },
      // ===================================================================
    // SET 6: STUDY & WORK ADVICE (B1)
    // ===================================================================
    {
      id: "m6-u5-s6",
      title: "Set 6: Study & Work Tips",
      context: `*Toronto — Week 19, Friday afternoon*

Dan has problems at work. He arrives late every day. He doesn't speak in meetings. He spends too much time on his phone. His manager spoke to him yesterday.
"What should I do?" he asks Sofía.
Sofía takes a deep breath. "Dan, I have a lot of advice. Do you want all of it?"
"Yes," he says.
"OK then. You should sit down."

*Toronto — Semana 19, viernes por la tarde*

Dan tiene problemas en el trabajo. Llega tarde todos los días. No habla en las reuniones. Pasa demasiado tiempo con su teléfono. Su jefe habló con él ayer.
"¿Qué debería hacer?" le pregunta a Sofía.
Sofía respira profundo. "Dan, tengo mucho consejo. ¿Quieres todo?"
"Sí," dice él.
"Bien. Deberías sentarte."`,

      questions: [
        {
          type: "mc",
          q: "'I failed my English test.' What is natural advice?",
          options: [
            "You should study harder next time.",
            "You should studied harder.",
            "You might study harder."
          ],
          a: "You should study harder next time.",
          tip: "Academic advice = SHOULD. 'Next time' makes it forward-looking and constructive.",
          successTip: "'You should study harder next time' = deberías estudiar más la próxima vez 📚. 'Next time' makes the advice positive — it's about the future, not just criticizing the past. 'Study harder' = estudiar más / con más esfuerzo. SHOULD for academic advice is extremely common — teachers and friends use it constantly!"
        },
        {
          type: "order",
          q: "Traduce: Deberías salir de casa más temprano.",
          words: ["You", "should", "leave", "home", "earlier."],
          a: "You should leave home earlier.",
          extra: ["must", "might", "to"],
          tip: "'Leave home' = salir de casa. 'Earlier' = más temprano. Punctuality advice.",
          successTip: "'Leave home earlier' = salir de casa más temprano ⏰. Very practical advice for someone who is always late. 'Earlier' is the comparative of 'early'. 'Leave earlier' / 'arrive earlier' / 'start earlier' — 'earlier' is one of the most useful words in time advice. Simple, direct, and very commonly used!"
        },
        {
          type: "input",
          q: "'I can't focus during meetings.' You ___ put your phone away.",
          a: "should",
          alts: ["Should"],
          tip: "Can't focus = phone is the problem. Putting it away = the clear solution = SHOULD.",
          successTip: "'Put your phone away' = guardar el teléfono 📱. 'Put away' = guardar / dejar a un lado. 'Put away your phone / books / things.' Extremely common phrasal verb! 'You should put your phone away during meetings' = natural, practical workplace advice. Direct but not rude!"
        },
        {
          type: "mc",
          q: "'I want a promotion at work.' What do you advise?",
          options: [
            "You should speak up in meetings.",
            "You should spoke up.",
            "You might speak up."
          ],
          a: "You should speak up in meetings.",
          tip: "'Speak up' = expresar tu opinión. SHOULD for clear career advice.",
          successTip: "'Speak up' = hablar / expresar tu opinión 🗣️. Very important in North American work culture! Managers notice employees who contribute ideas. 'Speak up in meetings / speak up for yourself / speak up when you disagree.' SHOULD + speak up = common career advice. Much more impactful than staying quiet!"
        },
        {
          type: "order",
          q: "Traduce: Deberías hablar con él directamente. Es mejor que por mensaje.",
          words: ["You", "should", "talk", "to", "him", "directly.", "It's", "better", "than", "by", "message."],
          a: "You should talk to him directly. It's better than by message.",
          extra: ["must", "might", "have"],
          tip: "Direct communication advice. Two sentences: advice + reason.",
          successTip: "Direct communication advice 💬. 'Talk to him directly' = hablar con él en persona / directamente. The second sentence explains WHY — 'It's better than by message.' This advice + reason structure is very natural. In Canadian and American work culture, direct communication is highly valued. Very useful real-world advice!"
        },
        {
          type: "mc",
          q: "'I'm behind on my project.' What is the advice?",
          options: [
            "You should catch up this weekend.",
            "You should caught up.",
            "You might catch up."
          ],
          a: "You should catch up this weekend.",
          tip: "'Catch up' = ponerse al día. SHOULD for clear advice about falling behind.",
          successTip: "'Catch up' = ponerse al día 📊. 'Catch up on work / homework / email / studying.' 'I'm behind' = estoy atrasado/a. 'You should catch up this weekend' = practical advice with a specific time. 'Catch up' is a very common phrasal verb in academic and work contexts. 'Behind' + 'catch up' = natural pair!"
        },
        {
          type: "input",
          q: "'I don't understand this topic.' You ___ ask your teacher for help.",
          a: "should",
          alts: ["Should"],
          tip: "Not understanding = the solution is clear (ask for help) = SHOULD.",
          successTip: "'Ask for help' = pedir ayuda 🙋. This seems obvious but many students don't do it! 'You should ask your teacher for help' = clear, caring advice. 'Ask for help' / 'ask a question' / 'ask someone' — all use 'ask'. 'Ask for' + noun = very common. 'Ask for help / advice / directions / a favour.'"
        },
        {
          type: "order",
          q: "Traduce: Deberías anotar las cosas importantes en clase.",
          words: ["You", "should", "write", "down", "important", "things", "in", "class."],
          a: "You should write down important things in class.",
          extra: ["must", "might", "to"],
          tip: "'Write down' = anotar. Study advice with a phrasal verb.",
          successTip: "'Write down' = anotar / escribir ✍️. 'Write down important things / your ideas / new vocabulary / the instructions.' Very useful phrasal verb! Compare: 'write' = just the act of writing. 'Write down' = record something so you don't forget it. Small difference — big meaning. Use 'write down' for notes!"
        },
        {
          type: "mc",
          q: "'My manager doesn't respect me.' What is the advice?",
          options: [
            "You should stand up for yourself.",
            "You should stood up.",
            "You might stand up."
          ],
          a: "You should stand up for yourself.",
          tip: "'Stand up for yourself' = defenderte. SHOULD for important workplace advice.",
          successTip: "'Stand up for yourself' = defenderte a ti mismo/a 💪. Very important phrase! 'Stand up for' = defender. 'Stand up for yourself / your rights / your colleague.' SHOULD here shows this is genuinely important — it's more than a suggestion. In modern workplace culture, advocating for yourself is essential and respected!"
        },
        {
          type: "match",
          q: "Match the work/study problems to the correct advice:",
          pairs: [
            { left: "Always late to work", right: "should leave earlier" },
            { left: "Can't focus in meetings", right: "should put phone away" },
            { left: "Behind on project", right: "should catch up" }
          ],
          a: "completed",
          successTip: "Work and study advice toolkit complete! 🎯 Leave earlier / put phone away / catch up / speak up / write down / stand up for yourself — six essential phrasal verbs for professional life. These aren't just grammar examples — they're the phrases you'll actually use and hear in real English workplaces and classrooms!"
        }
      ]
    },

    // ===================================================================
    // SET 7: SOCIAL & RELATIONSHIP ADVICE (B1)
    // ===================================================================
    {
      id: "m6-u5-s7",
      title: "Set 7: Social Advice",
      context: `*Toronto — Week 20, Sunday afternoon*

Amara is having a hard time. Her best friend from home stopped replying to her messages three weeks ago. She doesn't know why.
"I feel terrible," she tells Sofía and Priya.
"You should reach out one more time," says Sofía.
"And you should give her some space after that," adds Priya.
"What if she doesn't reply?"
"Then," says Sofía gently, "you should talk to us. We're here."

*Toronto — Semana 20, domingo por la tarde*

Amara está pasando un momento difícil. Su mejor amiga de su ciudad natal dejó de responder sus mensajes hace tres semanas. No sabe por qué.
"Me siento terrible," le dice a Sofía y Priya.
"Deberías intentar escribirle una vez más," dice Sofía.
"Y después deberías darle algo de espacio," añade Priya.
"¿Y si no responde?"
"Entonces," dice Sofía con dulzura, "deberías hablar con nosotras. Estamos aquí."`,

      questions: [
        {
          type: "mc",
          q: "'My friend is not replying to my messages.' What is natural advice?",
          options: [
            "You should reach out one more time.",
            "You should reached out.",
            "You might reach out."
          ],
          a: "You should reach out one more time.",
          tip: "'Reach out' = contactar a alguien. SHOULD for clear relationship advice.",
          successTip: "'Reach out' = contactar / comunicarse con alguien 📞. Very common modern phrase! 'Reach out to a friend / colleague / someone you miss.' 'One more time' makes the advice specific and measured — not desperate. SHOULD here shows genuine caring advice, not just a vague suggestion. Very authentic!"
        },
        {
          type: "order",
          q: "Traduce: Deberías disculparte con ella.",
          words: ["You", "should", "apologize", "to", "her."],
          a: "You should apologize to her.",
          extra: ["must", "might", "have"],
          tip: "'Apologize to someone' = disculparse con alguien. SHOULD for relationship repair.",
          successTip: "'Apologize to her' = disculparse con ella 🙏. 'Apologize TO someone' — note the preposition 'to'. Not 'apologize her' ❌. 'I apologize to you / to him / to everyone.' SHOULD for conflict repair is very natural. It's not a command — it's genuine advice from someone who cares about the relationship!"
        },
        {
          type: "input",
          q: "'I never have time for my friends.' You ___ make time for them.",
          a: "should",
          alts: ["Should"],
          tip: "Relationships need effort. Making time = clearly the right thing = SHOULD.",
          successTip: "'Make time for someone' = hacer tiempo para alguien ⏰. 'Make time' (not 'find time' — though both work) is the more active, intentional phrase. 'You should make time for your friends / family / yourself.' SHOULD shows this is genuinely important — not just optional. Relationships need active investment!"
        },
        {
          type: "mc",
          q: "'My roommate is always messy and it bothers me.' What do you advise?",
          options: [
            "You should talk it out with them.",
            "You should talked it out.",
            "You might talk it out."
          ],
          a: "You should talk it out with them.",
          tip: "'Talk it out' = hablarlo hasta resolverlo. SHOULD for conflict resolution advice.",
          successTip: "'Talk it out' = hablar el problema hasta resolverlo 💬. 'Talk out a problem / talk it out / talk things out.' Very North American advice pattern — direct communication is highly valued. SHOULD here is strong but caring: this IS the right thing to do. Avoiding the problem makes it worse. Much better to talk!"
        },
        {
          type: "order",
          q: "Traduce: Deberías salir más y conocer gente nueva.",
          words: ["You", "should", "go", "out", "more", "and", "meet", "new", "people."],
          a: "You should go out more and meet new people.",
          extra: ["must", "might", "to"],
          tip: "Two connected pieces of advice: go out AND meet people. Both base verbs.",
          successTip: "'Go out more and meet new people' = classic advice for loneliness or shyness 👥. Two actions connected with 'and' — both base verbs after SHOULD. 'Go out more' = socializar más. 'Meet new people' = conocer gente nueva. This advice is encouraging and positive — it opens doors rather than criticizing!"
        },
        {
          type: "mc",
          q: "'I always argue with my family. I get too angry.' What is the advice?",
          options: [
            "You should cool off before talking.",
            "You should cooled off.",
            "You might cool off."
          ],
          a: "You should cool off before talking.",
          tip: "'Cool off' = calmarse. SHOULD for important emotional advice.",
          successTip: "'Cool off' = calmarse / bajarse el enojo 😤→😌. 'Cool off before talking / arguing / responding.' Very wise advice! When you're angry, words come out wrong. SHOULD here is strong because this is genuinely important for relationships. 'Cool off' is also used for weather: 'It cooled off in the evening' — same idea: reduce heat!"
        },
        {
          type: "input",
          q: "'My friend never listens to me.' You ___ talk to someone you trust.",
          a: "should",
          alts: ["Should"],
          tip: "Not feeling heard = find someone who listens = clear advice = SHOULD.",
          successTip: "'Talk to someone you trust' = hablar con alguien de confianza 💭. 'Trust' as a verb: 'I trust you' / 'someone you trust.' Very important in emotional support advice. SHOULD here is genuinely caring — it's saying: you deserve to be heard, go find that person. Very empathetic use of SHOULD!"
        },
        {
          type: "order",
          q: "Traduce: Deberías ser más abierto con las personas que te quieren.",
          words: ["You", "should", "be", "more", "open", "with", "people", "who", "care", "about", "you."],
          a: "You should be more open with people who care about you.",
          extra: ["must", "might", "have"],
          tip: "'Be open with someone' = ser abierto/sincero. 'Care about' = importar alguien.",
          successTip: "'Be more open with people who care about you' = ser más abierto con las personas que te quieren 💛. 'Care about someone' = preocuparse por / querer a alguien. 'Open' here means willing to share feelings. 'Be open with' = ser honesto y vulnerable. SHOULD + 'be more...' = advice for personality/attitude changes!"
        },
        {
          type: "mc",
          q: "'I don't feel comfortable around my new colleagues.' What is gentle advice?",
          options: [
            "You should give it some time.",
            "You must feel comfortable immediately.",
            "You shouldn't work there."
          ],
          a: "You should give it some time.",
          tip: "'Give it some time' = darle tiempo. Patient, realistic, caring advice.",
          successTip: "'Give it some time' = darle tiempo ⏳. One of the most compassionate pieces of advice in English. You're saying: this is normal, it will get better, don't panic. 'Should give it some time' is realistic AND encouraging. Much better than 'you must feel comfortable' (impossible to force) or overly dramatic suggestions!"
        },
        {
          type: "match",
          q: "Match the social problems to the natural advice:",
          pairs: [
            { left: "Friend is upset with you", right: "should apologize" },
            { left: "Conflict with roommate", right: "should talk it out" },
            { left: "Too angry to talk", right: "should cool off first" }
          ],
          a: "completed",
          successTip: "Social advice toolkit complete! 🤝 Apologize / reach out / talk it out / cool off / make time / give it time — six essential phrases for relationship advice. These are the exact phrases native speakers use. Now you can give genuinely helpful, natural advice in any interpersonal situation in English!"
        }
      ]
    },

    // ===================================================================
    // SET 8: PRACTICAL EVERYDAY ADVICE (B1)
    // ===================================================================
    {
      id: "m6-u5-s8",
      title: "Set 8: Practical Advice",
      context: `*Toronto — Week 20, Thursday evening*

Leo is making big life decisions. He wants to travel to Europe, but he has no savings. He wants to buy a new laptop, but his credit card is almost full. He wants to move to a bigger apartment, but the rent is very expensive.
"Leo," Sofía says, "I should give you some financial advice."
"Please," he says.
"You should stop spending money first."
He pauses. "That's... actually good advice."

*Toronto — Semana 20, jueves por la noche*

Leo está tomando grandes decisiones de vida. Quiere viajar a Europa, pero no tiene ahorros. Quiere comprar una laptop nueva, pero su tarjeta de crédito está casi al límite. Quiere mudarse a un apartamento más grande, pero la renta es muy cara.
"Leo," dice Sofía, "debería darte algunos consejos financieros."
"Por favor," dice él.
"Primero deberías dejar de gastar dinero."
Hace una pausa. "Eso es... realmente buen consejo."`,

      questions: [
        {
          type: "mc",
          q: "'I'm always short of money.' What is the best advice?",
          options: [
            "You should save a little every month.",
            "You should saved money.",
            "You might save money."
          ],
          a: "You should save a little every month.",
          tip: "Financial problem → practical advice = SHOULD. 'A little every month' is realistic.",
          successTip: "'You should save a little every month' = deberías ahorrar un poco cada mes 💰. 'A little every month' is realistic and achievable — much better than 'save everything!' SHOULD for financial advice is very common. 'Save a little / spend less / make a budget' — practical, non-judgmental advice that is genuinely helpful!"
        },
        {
          type: "order",
          q: "Traduce: Deberías hacer un presupuesto. Es más fácil de lo que piensas.",
          words: ["You", "should", "make", "a", "budget.", "It's", "easier", "than", "you", "think."],
          a: "You should make a budget. It's easier than you think.",
          extra: ["must", "might", "have"],
          tip: "'Make a budget' = hacer un presupuesto. Encouraging second sentence.",
          successTip: "'Make a budget' = hacer un presupuesto 📊. The second sentence 'It's easier than you think' is encouraging — it removes the intimidation. This is great advice-giving technique: give the advice + make it sound achievable. 'It's easier than you think' / 'It doesn't take long' / 'You can do it' — all encourage action!"
        },
        {
          type: "input",
          q: "'I have a lot of credit card debt.' You ___ pay it off as soon as possible.",
          a: "should",
          alts: ["Should"],
          tip: "Credit card debt = clear financial problem. Paying it off = the obvious advice.",
          successTip: "'Pay off' = pagar una deuda completamente 💳. 'Pay off a debt / loan / credit card / mortgage.' 'As soon as possible' = lo antes posible. Very commonly used phrase! SHOULD + pay off + as soon as possible = strong but caring financial advice. The urgency is in 'as soon as possible', not in the modal."
        },
        {
          type: "mc",
          q: "'I want to travel to Europe but I have no savings.' What do you advise?",
          options: [
            "You should start saving now.",
            "You should saved up.",
            "You must travel."
          ],
          a: "You should start saving now.",
          tip: "Big goal + no money = start saving = practical advice = SHOULD.",
          successTip: "'Start saving now' = empezar a ahorrar ahora 🌍. 'Should + start + verb-ing' = advice to begin a new habit. 'You should start saving / exercising / studying / sleeping earlier.' This pattern is very natural for advice about habits you need to develop. 'Now' adds urgency — don't wait!"
        },
        {
          type: "order",
          q: "Traduce: Deberías dejar de desperdiciar dinero en cosas que no necesitas.",
          words: ["You", "should", "stop", "wasting", "money", "on", "things", "you", "don't", "need."],
          a: "You should stop wasting money on things you don't need.",
          extra: ["must", "might", "have"],
          tip: "'Stop + verb-ing' = dejar de hacer algo. SHOULD + stop + verb-ing = advice to stop a habit.",
          successTip: "'Should + stop + verb-ing' = advice to stop a bad habit ⛔. 'You should stop wasting / spending / eating / smoking / skipping.' SHOULD + stop + -ing is one of the most useful advice patterns! Compare: 'You should start saving' (new habit) vs 'You should stop spending' (stopping a bad habit). Both = SHOULD!"
        },
        {
          type: "mc",
          q: "'My car keeps breaking down.' What is practical advice?",
          options: [
            "You should get it repaired.",
            "You should got it repaired.",
            "You might get it repaired."
          ],
          a: "You should get it repaired.",
          tip: "Broken car = repair it = practical clear advice = SHOULD.",
          successTip: "'Get it repaired' = mandarlo a reparar 🔧. 'Get + object + past participle' = causative — you arrange for someone else to do it. 'Get your car repaired / get your phone fixed / get your hair cut.' Very natural for services. SHOULD + get + noun + repaired/fixed = extremely common practical advice. Learn this pattern!"
        },
        {
          type: "input",
          q: "'I lost my passport.' You ___ report it immediately.",
          a: "should",
          alts: ["Should"],
          tip: "Lost important document = report it fast. Clear urgent advice = SHOULD.",
          successTip: "'You should report it immediately' = deberías reportarlo de inmediato 🆘. 'Report' = reportar / avisar a las autoridades. 'Immediately' = inmediatamente — adds urgency. Note: for a real emergency (fire, accident), MUST would be stronger. But for a lost passport — serious but not life-threatening — SHOULD is appropriate!"
        },
        {
          type: "order",
          q: "Traduce: Deberías buscar asesoramiento profesional antes de invertir.",
          words: ["You", "should", "get", "professional", "advice", "before", "investing."],
          a: "You should get professional advice before investing.",
          extra: ["must", "might", "have"],
          tip: "'Before + verb-ing' = antes de + infinitivo. Logical sequence in advice.",
          successTip: "'Before + verb-ing' = antes de hacer algo 📈. 'Before investing / buying / signing / deciding.' This structure adds an important condition to the advice. 'You should do X before doing Y' = very logical, responsible advice. 'Get professional advice' = buscar consejo profesional. Essential for big financial decisions!"
        },
        {
          type: "mc",
          q: "'I want to move to a bigger apartment but rent is very expensive.' What is realistic advice?",
          options: [
            "You should compare different neighborhoods first.",
            "You should moved immediately.",
            "You must move right now."
          ],
          a: "You should compare different neighborhoods first.",
          tip: "'Compare first' = research before deciding. Practical, sensible SHOULD advice.",
          successTip: "'Compare different neighborhoods first' = comparar diferentes zonas primero 🏙️. Great advice structure: 'You should + research/compare/check + first.' 'First' suggests a sequence — do this before the next step. Very practical, non-pressuring advice. In Toronto especially, neighborhood research is essential before renting. Very realistic!"
        },
        {
          type: "match",
          q: "Match the practical problems to the correct advice:",
          pairs: [
            { left: "No savings", right: "should start saving now" },
            { left: "Broken car", right: "should get it repaired" },
            { left: "Too much spending", right: "should stop wasting money" }
          ],
          a: "completed",
          successTip: "Practical advice patterns complete! 💡 Start saving / make a budget / pay off debt / get it repaired / report it / get professional advice. These are real-world phrases you need for everyday life conversations. SHOULD + practical action = the most useful advice structure in English. Master this and you can help anyone!"
        }
      ]
    },

    // ===================================================================
    // SET 9: SOFTENING & NATURAL ADVICE (B1+)
    // ===================================================================
    {
      id: "m6-u5-s9",
      title: "Set 9: Giving Advice Naturally",
      context: `*Toronto — Week 21, coffee shop*

Professor Chen is having coffee with Sofía. They talk about how different cultures give advice.
"In some cultures," the professor says, "advice is very direct: 'Do this.' In North American English, we soften advice."
"How?" asks Sofía.
"We say 'maybe you should', or 'I think you should', or 'if I were you'. All of these are SHOULD — but gentler."
"In Mexico," Sofía smiles, "my grandmother just says: 'Do it. Now.'"
Professor Chen laughs. "Also effective."

*Toronto — Semana 21, cafetería*

La profesora Chen toma café con Sofía. Hablan sobre cómo diferentes culturas dan consejos.
"En algunas culturas," dice la profesora, "el consejo es muy directo: 'Haz esto.' En el inglés norteamericano, suavizamos el consejo."
"¿Cómo?" pregunta Sofía.
"Decimos 'maybe you should', 'I think you should', o 'if I were you'. Todos usan SHOULD — pero más suavemente."
"En México," sonríe Sofía, "mi abuela simplemente dice: 'Hazlo. Ahora.'"
La profesora Chen se ríe. "También funciona."`,

      questions: [
        {
          type: "mc",
          q: "Which piece of advice sounds the MOST polite and gentle?",
          options: [
            "Maybe you should talk to him.",
            "You should talk to him.",
            "You must talk to him."
          ],
          a: "Maybe you should talk to him.",
          tip: "'Maybe' softens SHOULD — it makes advice less direct and more considerate.",
          successTip: "Softening SHOULD with 'maybe' 🤗. 'Maybe you should...' = tal vez deberías... This is less direct than 'you should' — it suggests rather than recommends. Very common in North American conversation! Adding 'maybe' makes you sound thoughtful and considerate rather than bossy. Perfect when you're not 100% sure your advice is right!"
        },
        {
          type: "order",
          q: "Traduce: Tal vez deberías ver a un médico. Parece serio.",
          words: ["Maybe", "you", "should", "see", "a", "doctor.", "It", "seems", "serious."],
          a: "Maybe you should see a doctor. It seems serious.",
          extra: ["must", "might", "have"],
          tip: "'Maybe' + SHOULD = soft but genuine concern. 'Seems' = parece.",
          successTip: "'Maybe you should see a doctor. It seems serious.' 🩺. 'Seems' = parece — slightly uncertain. 'It seems serious' = parece serio (you're not diagnosing, just observing). 'Maybe + SHOULD + reason' = the ideal structure for caring but non-pushy health advice. Very natural when you're genuinely worried about someone!"
        },
        {
          type: "mc",
          q: "'I think you should take a break.' What does 'I think' add?",
          options: [
            "It softens the advice — it's your personal opinion, not a rule.",
            "It makes the advice stronger.",
            "It changes the meaning of SHOULD completely."
          ],
          a: "It softens the advice — it's your personal opinion, not a rule.",
          tip: "'I think you should...' = this is my personal opinion. Softer than direct 'you should'.",
          successTip: "'I think you should...' = creo que deberías... 💭. 'I think' signals personal opinion — you're sharing advice from your perspective, not stating a fact. 'I think you should apply for that job / take a break / talk to her.' Very empathetic and natural. Natives use 'I think you should' constantly in caring conversations!"
        },
        {
          type: "input",
          q: "Complete naturally: '___ you should apply for that job. You're perfect for it.'",
          a: "I think",
          alts: ["Maybe", "maybe", "i think"],
          tip: "'I think' or 'Maybe' both soften the advice here. Either is natural.",
          successTip: "'I think you should apply' / 'Maybe you should apply' — both perfect softeners 🎯. The following sentence 'You're perfect for it' adds encouragement — making the advice positive and motivating. Soft advice + encouragement = the most effective and caring way to advise someone in English. Very authentic!"
        },
        {
          type: "mc",
          q: "'If I were you, I would talk to her directly.' This means the same as:",
          options: [
            "I think you should talk to her directly.",
            "I am going to talk to her directly.",
            "Maybe I should talk to her directly."
          ],
          a: "I think you should talk to her directly.",
          tip: "'If I were you, I would...' = personal advice from your perspective. Same idea as SHOULD.",
          successTip: "'If I were you, I'd...' = yo que tú... / yo en tu lugar... 👤. This is a very natural, personal way to give advice. It shows empathy — you're imagining yourself in their situation. It means exactly the same as 'I think you should...' but feels more personal and connected. Very common in everyday English advice!"
        },
        {
          type: "order",
          q: "Traduce: Yo que tú, hablaría con ella hoy.",
          words: ["If", "I", "were", "you,", "I", "would", "talk", "to", "her", "today."],
          a: "If I were you, I would talk to her today.",
          extra: ["should", "must", "might"],
          tip: "'If I were you, I would...' = fixed expression for personal advice. Learn it as a chunk.",
          successTip: "'If I were you, I would...' = learn this as a complete chunk! 📌 Don't overthink the grammar — just know this phrase means 'yo que tú, haría...' and use it for personal, empathetic advice. 'If I were you, I'd apply / leave earlier / call her / save more.' Very frequently used in natural English conversation!"
        },
        {
          type: "mc",
          q: "Which is the MOST direct and firm piece of advice?",
          options: [
            "You should stop smoking.",
            "Maybe you should think about smoking less.",
            "You might want to consider smoking less."
          ],
          a: "You should stop smoking.",
          tip: "Direct SHOULD without softeners = the firmest form of advice. Clear and strong.",
          successTip: "Advice directness scale 📊: 'You might want to consider...' (very soft, indirect). 'Maybe you should think about...' (soft, considering). 'You should stop...' (direct, firm). All three are polite — they just have different levels of directness. Choose based on how serious the situation is and how well you know the person!"
        },
        {
          type: "input",
          q: "'I need advice.' Sofía replies: '___ you should talk to someone you trust.'",
          a: "I think",
          alts: ["Maybe", "maybe", "i think"],
          tip: "'I think' or 'Maybe' both work to soften caring advice.",
          successTip: "Empathetic advice opener 💛. 'I think you should talk to someone you trust' — combining 'I think' (personal, caring) with 'someone you trust' (emotional awareness). This is the language of genuine friendship. Not bossy, not cold — warm and personal. This is how good friends give advice in English!"
        },
        {
          type: "match",
          q: "Match the softening technique to the example:",
          pairs: [
            { left: "'Maybe' + SHOULD", right: "Maybe you should rest." },
            { left: "'I think' + SHOULD", right: "I think you should call her." },
            { left: "'If I were you'", right: "If I were you, I'd leave now." }
          ],
          a: "completed",
          successTip: "Three advice softeners mastered! 🎯 'Maybe you should...' / 'I think you should...' / 'If I were you, I'd...' These three patterns make your advice sound natural, considerate, and genuinely caring — not bossy or preachy. Using them shows cultural and linguistic intelligence. This is the difference between B1 and a native-level speaker!"
        },
        {
          type: "mc",
          q: "What is the most natural, caring response to 'I don't know what to do'?",
          options: [
            "I think you should take some time to think about it.",
            "You must decide immediately.",
            "You should have decided already."
          ],
          a: "I think you should take some time to think about it.",
          tip: "'Take some time to think' = realistic, patient advice. 'I think' softens it perfectly.",
          successTip: "'I think you should take some time to think about it' = creo que deberías tomarte tiempo para pensarlo 💭. Perfect advice when someone feels overwhelmed. It's patient, realistic, and non-pressuring. 'Take some time' = no rush. 'I think' = personal concern. This is exactly how a caring friend speaks in English. Beautiful, natural advice!"
        }
      ]
    },
  ],

  // ===================================================================
  // READING SET
  // ===================================================================
  readingSet: {
    id: "m6-u5-reading",
    title: "Reading: Dear Sofia",
    category: "reading",
    questions: [

      // ---------------------------------------------------------------
      // PASSAGE 1: Two letters
      // ---------------------------------------------------------------
      {
        type: "passage",
        content: `**Dear Sofia — An Advice Column**

*Sofía started a small advice column for the university newsletter. This week, she received two letters.*

---

**Letter 1 — From "Tired in Toronto"**

Dear Sofia,
I'm a first-year student and I'm exhausted. I study until 2am every night, I skip breakfast, I don't exercise, and I spend all weekend in the library. My grades are OK, but I feel terrible. What should I do?
— Tired in Toronto

---

**Carta 1 — De "Agotada en Toronto"**

Querida Sofía:
Soy estudiante de primer año y estoy agotada. Estudio hasta las 2am todas las noches, me salto el desayuno, no hago ejercicio y paso todo el fin de semana en la biblioteca. Mis calificaciones están bien, pero me siento terrible. ¿Qué debería hacer?
— Agotada en Toronto`
      },
      {
        type: "mc",
        q: "What is the main problem for 'Tired in Toronto'?",
        options: [
          "Her grades are bad.",
          "She studies hard but ignores her health and wellbeing.",
          "She doesn't like the library."
        ],
        a: "She studies hard but ignores her health and wellbeing.",
        tip: "Read carefully. She says grades are OK — the problem is how she feels.",
        successTip: "Reading comprehension: contrast 🎯. She says 'grades are OK BUT I feel terrible.' The word 'but' signals the real problem — not grades, but health and balance. This is very common in advice columns: the person thinks the problem is one thing, but the real issue is something else. Reading carefully reveals the true issue!"
      },
      {
        type: "input",
        q: "She studies until 2am. Sofia would say: 'You ___ go to bed so late.'",
        a: "shouldn't",
        alts: ["Shouldn't"],
        tip: "2am = very late for a student. Clearly a bad habit = SHOULDN'T.",
        successTip: "'You shouldn't go to bed so late' = no deberías acostarte tan tarde 🌙. The first and most obvious piece of advice for this student. 'So late' = tan tarde. SHOULDN'T for habits that are clearly harmful. This connects directly to all her other problems — sleep deprivation causes exhaustion, poor appetite, and difficulty focusing. Everything is connected!"
      },
      {
        type: "mc",
        q: "Sofia gives three pieces of advice to 'Tired in Toronto'. Which set is most natural?",
        options: [
          "You should sleep more, eat breakfast, and take breaks.",
          "You should slept more and you should eating breakfast.",
          "You must sleep more. You must eat. You must exercise."
        ],
        a: "You should sleep more, eat breakfast, and take breaks.",
        tip: "Three parallel pieces of advice connected with commas and 'and'. SHOULD once.",
        successTip: "Multiple advice with one SHOULD 💡. 'You should X, Y, and Z.' You don't need to say SHOULD before every item — just once at the beginning, then list the advice with commas and 'and'. 'You should sleep more, eat breakfast, and take breaks.' Clean, natural, efficient. Much better than repeating SHOULD three times!"
      },

      // ---------------------------------------------------------------
      // PASSAGE 2: Second letter and Sofia's reply
      // ---------------------------------------------------------------
      {
        type: "passage",
        content: `**Letter 2 — From "Confused About Career"**

Dear Sofia,
I finished my degree last year but I don't have a job yet. I apply for jobs online every day but nobody replies. My friends all have jobs. I feel like a failure. What should I do?
— Confused About Career

---

**Sofia's Reply to Letter 2**

Dear Confused About Career,
First: you are NOT a failure. Job hunting is hard and it takes time. Here is my advice:

You should update your resume — make it specific for each job. You should also reach out to people in your field directly, not just apply online. And maybe you should ask a career advisor at the university for help. They're free and very useful.

Most importantly: you shouldn't compare yourself to your friends. Everyone moves at a different pace.
— Sofia

---

**Carta 2 — De "Confundida con mi Carrera"**

Querida Sofía:
Terminé mi carrera el año pasado pero todavía no tengo trabajo. Solicito empleos en línea todos los días pero nadie responde. Todos mis amigos tienen trabajo. Me siento un fracaso. ¿Qué debería hacer?
— Confundida con mi Carrera

---

**La Respuesta de Sofía a la Carta 2**

Querida Confundida con mi Carrera:
Primero: NO eres un fracaso. La búsqueda de empleo es difícil y toma tiempo. Aquí está mi consejo:

Deberías actualizar tu currículum — hacerlo específico para cada empleo. También deberías contactar directamente a personas en tu campo, no solo solicitar en línea. Y tal vez deberías pedirle ayuda a un asesor de carrera en la universidad. Son gratuitos y muy útiles.

Lo más importante: no deberías compararte con tus amigos. Todos avanzan a un ritmo diferente.
— Sofía`
      },
      {
        type: "mc",
        q: "Sofia uses 'maybe you should' for one piece of advice. Why?",
        options: [
          "Because asking a career advisor is a gentle suggestion, not an urgent requirement.",
          "Because she doesn't think it is good advice.",
          "Because SHOULD is wrong in that sentence."
        ],
        a: "Because asking a career advisor is a gentle suggestion, not an urgent requirement.",
        tip: "'Maybe' softens SHOULD. Sofia uses it for the suggestion she's less certain about.",
        successTip: "Advice nuance in action! 🎯 Sofia uses direct SHOULD for the most important advice (update resume, reach out). She uses 'maybe you should' for the career advisor — a useful but optional suggestion. This shows sophisticated advice-giving: vary the strength depending on how important or certain the advice is. Very natural!"
      },
      {
        type: "order",
        q: "Reconstruct Sofia's most important advice from the letter:",
        words: ["You", "shouldn't", "compare", "yourself", "to", "your", "friends."],
        a: "You shouldn't compare yourself to your friends.",
        extra: ["must", "might", "have"],
        tip: "SHOULDN'T for something to avoid. 'Compare yourself to' = compararse con.",
        successTip: "'You shouldn't compare yourself to your friends' = no deberías compararte con tus amigos 💛. 'Compare yourself to someone' = compararse con alguien. This is SHOULDN'T for emotional wellbeing advice — one of the most common and important uses. Sofia ends with this as the most important point. Emotional advice often saves the best for last!"
      },
      {
        type: "mc",
        q: "Sofia gives advice in three different ways. Which DOESN'T appear in her reply?",
        options: [
          "You must apply for exactly 50 jobs per week.",
          "You should update your resume.",
          "Maybe you should ask a career advisor."
        ],
        a: "You must apply for exactly 50 jobs per week.",
        tip: "Read Sofia's letter again. She never gives a specific number or uses MUST.",
        successTip: "Reading detail check 🔍. Sofia uses SHOULD (firm advice) and 'maybe you should' (gentle suggestion) — but never MUST. MUST would be too demanding and unrealistic for this sensitive situation. Good advice-givers know when NOT to use MUST. Sofia is empathetic and supportive — MUST would sound harsh and unhelpful here!"
      },

      // ---------------------------------------------------------------
      // PASSAGE 3: Sofia reflects
      // ---------------------------------------------------------------
      {
        type: "passage",
        content: `**Sofia Reflects on Giving Advice**

After a week of writing the column, Sofia talks to Priya about what she learned.

"Giving advice is harder than I thought," she says. "You have to be careful. If you're too direct, people feel judged. If you're too soft, they don't take you seriously."

"What's the secret?" Priya asks.

"I think the secret is: you should listen first. Really listen. Then you should think about what the person actually needs — not just what you think they should do. And sometimes, the best advice is just: 'You're doing fine. Keep going.'"

Priya smiles. "Maybe you should write a book."

Sofia laughs. "Maybe I should."

---

**Sofía Reflexiona sobre Dar Consejos**

Después de una semana escribiendo la columna, Sofía le habla a Priya sobre lo que aprendió.

"Dar consejos es más difícil de lo que pensaba," dice. "Hay que tener cuidado. Si eres demasiado directa, las personas se sienten juzgadas. Si eres demasiado suave, no te toman en serio."

"¿Cuál es el secreto?" pregunta Priya.

"Creo que el secreto es: deberías escuchar primero. De verdad escuchar. Después deberías pensar en lo que la persona realmente necesita — no solo en lo que crees que debería hacer. Y a veces, el mejor consejo es simplemente: 'Lo estás haciendo bien. Sigue adelante.'"

Priya sonríe. "Tal vez deberías escribir un libro."

Sofía se ríe. "Tal vez debería."`
      },
      {
        type: "mc",
        q: "According to Sofia, what is the secret to good advice?",
        options: [
          "Listen first, then think about what the person actually needs.",
          "Always use SHOULD and be very direct.",
          "Write an advice column every week."
        ],
        a: "Listen first, then think about what the person actually needs.",
        tip: "Read Sofia's direct quote. She says the secret in two clear steps.",
        successTip: "The two-step advice formula from Sofia 💡: 1) Listen first — really listen. 2) Think about what they actually need. This is real-world wisdom, not just grammar! 'You should listen first' = the meta-advice about giving advice. Very sophisticated. And it applies in English AND in any language — good advice always starts with good listening!"
      },
      {
        type: "input",
        q: "Priya says: 'Maybe you ___ write a book.' Sofia replies: 'Maybe I ___.'",
        a: "should",
        alts: ["Should"],
        tip: "Both use SHOULD — first in a soft suggestion, then in a short answer.",
        successTip: "'Maybe you should write a book.' / 'Maybe I should.' 📖. Perfect example of SHOULD in a short answer! Sofia doesn't repeat 'write a book' — she just says 'Maybe I should.' Efficient and natural. Also notice: Priya uses 'maybe you should' (soft suggestion) and Sofia echoes it — very natural conversational pattern in English!"
      },
      {
        type: "match",
        q: "Match Sofia's advice strategies to the correct example from the reading:",
        pairs: [
          { left: "Firm SHOULD advice", right: "You should update your resume." },
          { left: "Soft MAYBE + SHOULD", right: "Maybe you should ask a career advisor." },
          { left: "SHOULDN'T to avoid", right: "You shouldn't compare yourself to friends." }
        ],
        a: "completed",
        successTip: "Three SHOULD strategies in one reading! 🎓 Direct SHOULD (firm recommendation) + Maybe SHOULD (gentle suggestion) + SHOULDN'T (what to avoid). Real advice uses all three levels naturally. You've now seen SHOULD in authentic context — a real advice column. This is exactly how native speakers use SHOULD in writing and conversation. Unit 5 complete!"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET
  // ===================================================================
  reviewSet: {
    id: "m6-u5-review",
    title: "Review: Giving Advice with SHOULD",
    questions: [
      {
        type: "mc",
        q: "Your friend is sick. What is the best advice?",
        options: ["You should rest and drink water.", "You should rested.", "You must rest immediately."],
        a: "You should rest and drink water.",
        tip: "SHOULD for clear, friendly health advice. Two pieces with 'and' after one SHOULD.",
        successTip: "'You should rest and drink water' ✅ 💊. SHOULD + two actions with 'and' = efficient advice. 'Should rested' ❌ — wrong form. 'Must rest immediately' — too strong for a sick friend unless it's an emergency. SHOULD is the natural, caring choice for health advice between friends. Clean, correct, and kind!"
      },
      {
        type: "order",
        q: "Traduce: Tal vez deberías hablar con alguien de confianza.",
        words: ["Maybe", "you", "should", "talk", "to", "someone", "you", "trust."],
        a: "Maybe you should talk to someone you trust.",
        extra: ["must", "might", "have"],
        tip: "'Maybe' + SHOULD = soft, caring advice. 'Someone you trust' = alguien de confianza.",
        successTip: "'Maybe you should talk to someone you trust' 💛. Soft advice + 'someone you trust' = empathetic, genuine. 'Maybe' removes pressure. 'Someone you trust' shows emotional intelligence. This is what caring advice sounds like in English — not bossy, not vague, but warm and specific. One of the most useful advice sentences you can know!"
      },
      {
        type: "mc",
        q: "Which question is CORRECT when asking for advice?",
        options: ["Should I take the job?", "Do I should take the job?", "I should take the job?"],
        a: "Should I take the job?",
        tip: "SHOULD moves to the front in questions. Never 'do I should'.",
        successTip: "'Should I take the job?' ✅ = ¿Debería tomar el trabajo? 🤔. SHOULD moves to the front — no DO needed. 'Do I should...?' ❌ = the most common Spanish-speaker error with SHOULD questions. Modal verbs make their own questions by inversion. 'Should I / Should you / Should we / Should she' — all correct. Always!"
      },
      {
        type: "input",
        q: "You ___ eat so much sugar. It's not good for you.",
        a: "shouldn't",
        alts: ["Shouldn't"],
        tip: "Too much sugar = bad habit = advice to avoid = SHOULDN'T.",
        successTip: "'You shouldn't eat so much sugar' = no deberías comer tanto azúcar 🍬. SHOULDN'T for health warnings that are genuine, caring advice. 'So much' + uncountable noun. 'It's not good for you' = the reason. Advice + reason = the most natural, convincing advice structure. Short and effective!"
      },
      {
        type: "match",
        q: "Match the strength level to the correct modal:",
        pairs: [
          { left: "Law / obligation", right: "must" },
          { left: "Clear recommendation", right: "should" },
          { left: "Gentle suggestion", right: "might want to" }
        ],
        a: "completed",
        successTip: "The advice strength scale revisited 🎯. MUST (law/obligation) → SHOULD (recommendation) → MIGHT WANT TO (gentle suggestion). Choosing the right level makes you sound natural, culturally aware, and genuinely helpful. Too strong (MUST for small things) = bossy. Too weak (MIGHT for emergencies) = unhelpful. SHOULD = the perfect default!"
      },
      {
        type: "mc",
        q: "Which sentence has a grammatical error?",
        options: ["He shoulds go home.", "He should go home.", "Should he go home?"],
        a: "He shoulds go home.",
        tip: "Modal verbs NEVER add -s for third person. 'He should' — never 'he shoulds'.",
        successTip: "'He shoulds go home' ❌ → 'He should go home' ✅. No -s on modal verbs — ever! 'He should / she should / it should' — all the same. This applies to ALL modals: 'he wills' ❌ / 'she cans' ❌ / 'it mights' ❌. One of the most important grammar rules: modal verbs are invariable. Zero exceptions!"
      },
      {
        type: "order",
        q: "Traduce: Yo que tú, hablaría con ella hoy mismo.",
        words: ["If", "I", "were", "you,", "I", "would", "talk", "to", "her", "today."],
        a: "If I were you, I would talk to her today.",
        extra: ["should", "must", "might"],
        tip: "'If I were you, I would...' = fixed chunk for personal advice. Learn it as a phrase.",
        successTip: "'If I were you, I would talk to her today' 👤. This is your B1+ advice phrase — personal, empathetic, and very natural. 'If I were you' + 'I would' + base verb. Learn it as a complete chunk and use it when you want to give advice that feels personal and caring. Very frequently heard in real English conversation!"
      },
      {
        type: "mc",
        q: "'I think you should apply for that job.' What does 'I think' do?",
        options: [
          "It softens the advice — it's a personal opinion, not a rule.",
          "It makes the advice stronger and more urgent.",
          "It has no effect on the meaning."
        ],
        a: "It softens the advice — it's a personal opinion, not a rule.",
        tip: "'I think' = personal opinion marker. Softens SHOULD — more caring and less bossy.",
        successTip: "'I think you should...' = creo que deberías... 💭. 'I think' = your personal opinion. It humanizes the advice — you're not saying 'this is the rule', you're saying 'this is what I personally believe is best for you.' Much warmer than direct 'you should'. Natives use 'I think you should' for close friends and sensitive topics. Very empathetic!"
      },
      {
        type: "input",
        q: "Where ___ we go for dinner tonight? Any ideas?",
        a: "should",
        alts: ["Should"],
        tip: "Asking for a recommendation about dinner = SHOULD in a question.",
        successTip: "'Where should we go for dinner tonight?' = ¿Adónde deberíamos ir a cenar esta noche? 🍽️. Perfect everyday question! 'Where should we...?' for group decisions. 'What should I...?' for personal decisions. 'Should I...?' for yes/no decisions. Three question patterns — all use SHOULD + inversion. Master these and you can ask for advice about anything!"
      },
      {
        type: "match",
        q: "Match the errors to their corrections:",
        pairs: [
          { left: "You should to go", right: "You should go" },
          { left: "Do I should call?", right: "Should I call?" },
          { left: "She shoulds rest", right: "She should rest" }
        ],
        a: "completed",
        successTip: "Unit 5 complete! 🎉 Three rules, zero exceptions: 1) SHOULD + base verb (never 'to'). 2) Questions: SHOULD + subject (never 'do'). 3) No -s for any subject (never 'shoulds'). Fix these three errors and your SHOULD is perfect. You can now give advice, ask for advice, and soften advice naturally — at any level from A2 to B1+!"
      }
    ]
  }
};
